import type {
  HardwareCapabilities,
  HardwareResult,
  HardwareRuntimeAdapter,
} from "./contracts";

type NativeBridge = {
  getCapabilities(): string;
  printReceipt(payloadJson: string): string;
  openDrawer(reason: string): string;
};

type NativeBridgeWindow = Window & {
  MOREPOSNativeHardware?: NativeBridge;
};

const unavailableCapabilities: HardwareCapabilities = {
  receiptPrinter: false,
  productionPrinter: false,
  cashDrawer: false,
  barcodeScanner: false,
  customerDisplay: false,
  paymentDevice: false,
};

function bridge(): NativeBridge | null {
  if (typeof window === "undefined") return null;
  return (window as NativeBridgeWindow).MOREPOSNativeHardware ?? null;
}

function parseCapabilities(raw: string): HardwareCapabilities {
  try {
    const value = JSON.parse(raw) as Partial<HardwareCapabilities> | null;
    if (!value || typeof value !== "object") return unavailableCapabilities;
    return {
      receiptPrinter: value.receiptPrinter === true,
      productionPrinter: value.productionPrinter === true,
      cashDrawer: value.cashDrawer === true,
      barcodeScanner: value.barcodeScanner === true,
      customerDisplay: value.customerDisplay === true,
      paymentDevice: value.paymentDevice === true,
    };
  } catch {
    return unavailableCapabilities;
  }
}

function parseResult(raw: string): HardwareResult {
  try {
    const value = JSON.parse(raw) as {
      ok?: unknown;
      reason?: unknown;
      detail?: unknown;
    } | null;
    if (value?.ok === true) return { ok: true };
    const reason = value?.reason;
    if (reason === "UNSUPPORTED" || reason === "UNAVAILABLE" || reason === "FAILED") {
      const detail = value?.detail;
      return {
        ok: false,
        reason,
        detail: typeof detail === "string" ? detail : undefined,
      };
    }
  } catch {
    // Fall through to a safe bridge failure below.
  }
  return { ok: false, reason: "FAILED", detail: "Native hardware bridge returned an invalid response" };
}

export function hasAndroidNativeHardwareBridge() {
  return bridge() !== null;
}

export class AndroidNativeHardwareAdapter implements HardwareRuntimeAdapter {
  readonly id = "ANDROID_NATIVE_BRIDGE_V1";

  getCapabilities(): HardwareCapabilities {
    const native = bridge();
    if (!native) return unavailableCapabilities;
    try {
      return parseCapabilities(native.getCapabilities());
    } catch {
      return unavailableCapabilities;
    }
  }

  async printReceipt(payload: unknown): Promise<HardwareResult> {
    const native = bridge();
    if (!native) return { ok: false, reason: "UNAVAILABLE", detail: "Native hardware bridge is unavailable" };
    try {
      return parseResult(native.printReceipt(JSON.stringify(payload)));
    } catch (error) {
      return {
        ok: false,
        reason: "FAILED",
        detail: error instanceof Error ? error.message : "Native receipt command failed",
      };
    }
  }

  async openDrawer(reason: string): Promise<HardwareResult> {
    const native = bridge();
    if (!native) return { ok: false, reason: "UNAVAILABLE", detail: "Native hardware bridge is unavailable" };
    try {
      return parseResult(native.openDrawer(reason));
    } catch (error) {
      return {
        ok: false,
        reason: "FAILED",
        detail: error instanceof Error ? error.message : "Native drawer command failed",
      };
    }
  }
}
